import type React from 'react';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import Naptime from 'bundles/naptimejs';
import type {
  OnDemandSessionMembershipsV1,
  OnDemandSessionMembershipsV1ActiveByUserAndCourseQuery,
  OnDemandSessionMembershipsV1ActiveByUserAndCourseQueryVariables,
} from 'bundles/naptimejs/resources/__generated__/OnDemandSessionMembershipsV1';
import OnDemandSessionsV1 from 'bundles/naptimejs/resources/onDemandSessions.v1';

const getSessionsMembershipsQuery = gql`
  query GetSessionMemberships($courseId: String!, $userId: String!) {
    OnDemandSessionMembershipsV1 @naptime {
      activeByUserAndCourse(courseId: $courseId, userId: $userId) {
        elements {
          id
          createdAt
          sessionId
          userId
        }
      }
    }
  }
`;

type InputProps = {
  courseId: string;
};

type PropsFromGraphql = InputProps & {
  sessionMembership: OnDemandSessionMembershipsV1 | null;
};

export type PropsFromWithCurrentSession = {
  currentSession: OnDemandSessionsV1 | null;
  sessionMembership: OnDemandSessionMembershipsV1 | null;
};

export default <PropsFromCaller extends InputProps>(
  PassedComponent: React.ComponentType<PropsFromWithCurrentSession & PropsFromCaller>
): React.ComponentType<PropsFromCaller> =>
  compose<PropsFromWithCurrentSession & PropsFromCaller, PropsFromCaller>(
    waitForGraphQL<
      PropsFromCaller,
      OnDemandSessionMembershipsV1ActiveByUserAndCourseQuery,
      OnDemandSessionMembershipsV1ActiveByUserAndCourseQueryVariables,
      PropsFromGraphql
    >(getSessionsMembershipsQuery, {
      options: ({ courseId }) => ({
        variables: {
          courseId,
          userId: user.get().id.toString(),
        },
      }),
      props: ({ ownProps, data }): PropsFromGraphql => ({
        ...ownProps,
        sessionMembership: data?.OnDemandSessionMembershipsV1?.activeByUserAndCourse?.elements?.[0] ?? null,
      }),
    }),
    Naptime.createContainer<PropsFromWithCurrentSession, PropsFromGraphql>((propsFromGraphql) => {
      const { sessionMembership } = propsFromGraphql;
      // @ts-expect-error This property .sessionId exists, need to figure out what's happening with the type-generation that isn't populating it
      const sessionId = sessionMembership?.sessionId;
      return {
        currentSession: sessionId
          ? OnDemandSessionsV1.get(sessionId, {
              fields: ['courseId', 'branchId', 'startedAt', 'endedAt', 'isPrivate'],
            })
          : null,
      };
    })
  )(PassedComponent);
